import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Plane,
  Building2,
  Users,
  Wallet,
  CreditCard,
  User,
  LogOut,
  Menu,
  X,
  LifeBuoy,
  Ticket
} from 'lucide-react';
import { useAgentAuth } from '../../context/AgentAuthContext';
import { getAgentPaymentNavState } from '../../utils/agentPaymentNav';
import AgentBalanceBadge from '../../components/agent/AgentBalanceBadge';
import { useAgentSupportBadge } from '../../hooks/useAgentSupportBadge';

const AgentLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { agent, logout, refreshAgentBalances } = useAgentAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  React.useEffect(() => {
    if (!agent?.id) return;
    refreshAgentBalances();
  }, [agent?.id]);

  const handleLogout = () => {
    logout();
    navigate('/agent/login');
  };

  const { isCreditAgent, walletNavHidden, isHybrid } = getAgentPaymentNavState(agent);
  const { needReplyCount } = useAgentSupportBadge(Boolean(agent));

  const navItems = [
    { path: '/agent/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/agent/bookings', icon: Plane, label: 'Flight Bookings' },
    { path: '/agent/seat-blocks', icon: Ticket, label: 'My blocked seats' },
    { path: '/agent/hotel-bookings', icon: Building2, label: 'Hotel Bookings' },
    { path: '/agent/travellers', icon: Users, label: 'My Travellers' },
    ...(!walletNavHidden ? [{ path: '/agent/wallet', icon: Wallet, label: 'My Wallet' }] : []),
    ...(isCreditAgent ? [{ path: '/agent/credit', icon: CreditCard, label: 'My Credit' }] : []),
    { path: '/agent/reports', icon: LayoutDashboard, label: 'Reports' },
    { path: '/agent/commission', icon: Wallet, label: 'Commission' },
    { path: '/agent/support-tickets', icon: LifeBuoy, label: 'My Support' },
    { path: '/agent/profile', icon: User, label: 'My Profile' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden bg-[#1e3a5f] text-white p-4 flex items-center justify-between sticky top-0 z-50 shadow-md">
        <div className="flex items-center gap-3">
          <button onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <div>
            <h1 className="font-bold text-lg">ACF Tourism</h1>
            <p className="text-xs opacity-90">Agent Panel</p>
          </div>
        </div>
        <AgentBalanceBadge agent={agent} variant="compact" />
      </div>

      {/* Desktop Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-screen w-72 bg-white border-r border-gray-200
        transform transition-transform duration-300 z-40
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        {/* Logo */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
              {walletNavHidden && isCreditAgent ? (
                <CreditCard className="w-6 h-6 text-white" />
              ) : isHybrid ? (
                <div className="flex items-center gap-0.5">
                  <Wallet className="w-5 h-5 text-white" />
                  <CreditCard className="w-4 h-4 text-white/90" />
                </div>
              ) : (
                <Wallet className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h1 className="font-bold text-lg text-gray-900">Agent Panel</h1>
              <p className="text-xs text-gray-500">ACF Tourism</p>
            </div>
          </div>
        </div>

        <AgentBalanceBadge agent={agent} variant="card" />

        {/* Navigation */}
        <nav className="px-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.path === '/agent/support-tickets'
              ? location.pathname.startsWith('/agent/support-tickets')
              : location.pathname === item.path;
            const showBadge = item.path === '/agent/support-tickets' && needReplyCount > 0;

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl transition-all
                  ${isActive
                    ? 'bg-teal-50 text-teal-700 font-semibold shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
                data-testid={item.path === '/agent/support-tickets' ? 'nav-agent-support' : undefined}
              >
                <Icon size={20} className={isActive ? 'text-teal-600' : ''} />
                <span className="flex-1">{item.label}</span>
                {showBadge && (
                  <span
                    className="min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold"
                    data-testid="agent-support-nav-badge"
                  >
                    {needReplyCount > 99 ? '99+' : needReplyCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl transition-colors font-medium"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-72 min-h-screen">
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AgentLayout;
